import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatNumber } from '@column/column-ui-kit';

import {
  AccountNumber,
  BankAccount,
  BankAccountFilterParams,
  BankAccountListResponse,
  BankAccountRepository,
  ListBankAccountActivitiesRequest,
  ListBankAccountActivitiesResponse,
} from '~/repositories/BankAccountRepository';
import { useSessionStore } from '~/stores/Session';
import { BankAccountWithDetails } from '~/typings/API';

import { createApiHook } from './utils/createApiHook';

export type ActivityFilterType = ListBankAccountActivitiesRequest & {
  page?: number;
};

export type BankAccountFilterType = BankAccountFilterParams & {
  page?: number;
};

export const useBankAccounts = createApiHook<BankAccountListResponse, BankAccountFilterType>(
  BankAccountRepository.getAll,
  {
    triggerAutomatically: true,
    triggerOnSessionStoreSubscribe: true,
    includeQueryParams: true,
    excludeQueryParams: ['page'],
  }
);

export const useBankAccountsList = createApiHook<BankAccountListResponse, BankAccountFilterType>(
  BankAccountRepository.getAll,
  {
    triggerAutomatically: true,
    triggerOnSessionStoreSubscribe: true,
    includeQueryParams: true,
  }
);

export const useLazyBankAccounts = createApiHook<BankAccountListResponse, BankAccountFilterParams>(
  BankAccountRepository.getAll,
  {
    includeQueryParams: true,
  }
);

export const useBankAccount = createApiHook<BankAccountWithDetails, GetRequestType>(BankAccountRepository.get);

export const useAccountNumber = createApiHook<AccountNumber, GetRequestType>(BankAccountRepository.getAccountNumber);

export const useBankAccountActivity = (bankAccountId: string) =>
  createApiHook<ListBankAccountActivitiesResponse, ActivityFilterType>(
    (request) => BankAccountRepository.getActivity(bankAccountId, request),
    {
      triggerAutomatically: true,
      triggerOnSessionStoreSubscribe: true,
      includeQueryParams: true,
      addQueryParamsToUrl: true,
      excludeQueryParams: ['page'],
    }
  );

export const useBankAccountDropdown = (bankAccountId?: string, initialQueryParams?: BankAccountFilterParams) => {
  const platformId = useSessionStore((state) => state.currentPlatform?.id ?? state.currentUser?.defaultPlatformId);
  const [queryParams, setQueryParams] = useState<BankAccountFilterParams | undefined>(initialQueryParams);
  const prevInitialQueryParamsRef = useRef<string>();

  const initialQueryParamsString = useMemo(() => JSON.stringify(initialQueryParams), [initialQueryParams]);

  useEffect(() => {
    if (prevInitialQueryParamsRef.current !== initialQueryParamsString) {
      prevInitialQueryParamsRef.current = initialQueryParamsString;
      setQueryParams(initialQueryParams);
    }
  }, [initialQueryParamsString, initialQueryParams]);

  const bankAccountsQuery = useQuery({
    queryKey: ['bankAccountsList', platformId, queryParams],
    queryFn: () => BankAccountRepository.getAll({ ...queryParams, platformId }),
    enabled: Boolean(platformId),
  });

  const activeBankAccountQuery = useQuery({
    queryKey: ['bankAccount', platformId, bankAccountId],
    queryFn: () => BankAccountRepository.get({ id: bankAccountId!, platformId }),
    enabled: Boolean(platformId && bankAccountId),
  });

  const bankAccounts = bankAccountsQuery.data;
  const activeBankAccount = activeBankAccountQuery.data;
  const isInitialLoad = bankAccountsQuery.isLoading && bankAccountsQuery.isFetching;

  const getOption = useCallback(
    (bankAccount: BankAccount | undefined) =>
      bankAccount && {
        label: `${
          bankAccount?.displayName && bankAccount?.description
            ? `${bankAccount.displayName} – ${bankAccount.description}`
            : bankAccount?.description || 'Unnamed'
        }`,
        small: `${formatNumber(bankAccount?.balances?.availableAmount)}`,
        value: bankAccount.id,
      },
    []
  );

  const searchBankAccounts = useCallback((params?: BankAccountFilterParams) => {
    setQueryParams((prev) => ({ ...prev, ...params }));
  }, []);

  const options = useMemo(
    () =>
      (bankAccounts?.bankAccounts ?? [])
        .filter(Boolean)
        .map(getOption)
        .filter((option) => option !== undefined) ?? [],
    [bankAccounts, getOption]
  );

  const hiddenOptions = useMemo(
    () =>
      options.some((option) => option.value === activeBankAccount?.id)
        ? []
        : ([activeBankAccount]
            .filter(Boolean)
            .map(getOption)
            .filter((option) => option !== undefined) ?? []),
    [activeBankAccount, getOption, options]
  );

  const activeAccount =
    bankAccounts?.bankAccounts?.find((account) => account.id === bankAccountId) || activeBankAccount;

  return { bankAccounts, options, hiddenOptions, searchBankAccounts, isLoading: isInitialLoad, activeAccount };
};
